import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import io from "socket.io-client";
import ApiMethods from "../../ApiManager/ApiMethods";
import NotificationsDrawer from "../Screens/secure/components/NotificationsDrawer";
import { toDisplayNotification } from "../Screens/secure/components/notificationDisplay";
import { playNotificationSound } from "../../common/utils/notificationSound";

const NotificationsContext = createContext();

// Office notifications are broadcast (no per-employee targeting yet — see the notifications
// migration), so every employee session just asks for the whole `employee` feed.
const RECIPIENT_TYPE = "employee";

// Same connection convention as SocketNotifications.js — same origin/proxy in dev.
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || undefined;

// In-flight-request dedup, module-level since NotificationsProvider is a single app-root
// singleton (mounted once in App.js) — same problem/fix as membersStore.js's
// `ensureMembersLoaded`/`inFlightPromise`: in dev, React 18 StrictMode double-invokes effects
// on mount, so the mount effect below called `fetchNotifications()` twice back to back, firing
// two identical `notifications?recipient_type=employee` requests every app load.
let inFlightNotificationsPromise = null;

export const NotificationsProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const navigate = useNavigate();

  const unreadCount = notifications.filter((n) => !n.read).length;

  const fetchNotifications = useCallback(async () => {
    if (inFlightNotificationsPromise) return inFlightNotificationsPromise;
    inFlightNotificationsPromise = ApiMethods.get(
      `notifications?recipient_type=${RECIPIENT_TYPE}`
    )
      .then((raw) => {
        setNotifications((raw || []).map(toDisplayNotification));
      })
      .catch((err) => {
        console.log("Error fetching notifications:", err);
      })
      .finally(() => {
        inFlightNotificationsPromise = null;
      });
    return inFlightNotificationsPromise;
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Load once on mount, then rely on the office room socket push for updates — no polling.
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return undefined;

    const socket = io(SOCKET_URL, {
      path: "/socket.io",
      transports: ["websocket"],
      auth: { token },
    });

    socket.on("notification:new", () => {
      fetchNotifications();
      playNotificationSound();
    });

    return () => {
      socket.disconnect();
    };
  }, [fetchNotifications]);

  const openNotif = () => {
    setNotifOpen(true);
    fetchNotifications();
  };
  const closeNotif = () => setNotifOpen(false);

  const markAllRead = async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    try {
      await ApiMethods.post("notifications/read-all", { recipient_type: RECIPIENT_TYPE });
    } catch (err) {
      console.log("Error marking all notifications read:", err);
    }
  };

  const handleNotificationClick = async (n) => {
    setNotifications((prev) =>
      prev.map((item) => (item.id === n.id ? { ...item, read: true } : item))
    );
    setNotifOpen(false);
    if (n.route) navigate(n.route);
    try {
      await ApiMethods.post(`notifications/${n.id}/read`, {});
    } catch (err) {
      console.log("Error marking notification read:", err);
    }
  };

  return (
    <NotificationsContext.Provider value={{ unreadCount, openNotif }}>
      {children}
      <NotificationsDrawer
        open={notifOpen}
        onClose={closeNotif}
        notifications={notifications}
        onMarkAllRead={markAllRead}
        onNotificationClick={handleNotificationClick}
      />
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationsContext);
